import React, { useState } from 'react';
import { Plus, X, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { smallInputCls, btnSecondary } from './AdminUI';

const MAX_OPTIONS = 5;
const MAX_PERSONALIZATIONS = 5;
const OPTION_PRESETS = ['Size', 'Color', 'Style', 'Material', 'Scent'];

function ValueChip({ value, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-1 bg-ink/[0.04] border border-ink/10 rounded-lg pl-2 pr-1 py-1">
      <input value={value.label} onChange={e => onChange({ ...value, label: e.target.value })}
        className="bg-transparent text-ink text-xs w-20 focus:outline-none" />
      <span className="text-ink/45 text-xs">+$</span>
      <input type="number" step="0.01" value={value.price_delta || ''} placeholder="0"
        onChange={e => onChange({ ...value, price_delta: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
        className="bg-transparent text-ink text-xs w-12 focus:outline-none" title="Extra charge for this choice" />
      <button type="button" onClick={onRemove} className="text-ink/45 hover:text-red-600"><X className="w-3 h-3" /></button>
    </div>
  );
}

function OptionRow({ option, index, count, onChange, onRemove, onMove }) {
  const [draft, setDraft] = useState('');
  const addValues = () => {
    const labels = draft.split(',').map(v => v.trim()).filter(Boolean)
      .filter(l => !option.values.some(v => v.label.toLowerCase() === l.toLowerCase()));
    if (!labels.length) return;
    onChange({ ...option, values: [...option.values, ...labels.map(label => ({ label, price_delta: 0 }))] });
    setDraft('');
  };

  return (
    <div className="p-3 rounded-xl border border-ink/10 space-y-2">
      <div className="flex items-center gap-2">
        <input value={option.name} onChange={e => onChange({ ...option, name: e.target.value })} placeholder="Option name, e.g. Size"
          list="option-presets" className={`${smallInputCls} w-44`} />
        <div className="flex gap-1 ml-auto">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="p-1 text-ink/45 hover:text-ink disabled:opacity-20"><ArrowUp className="w-3 h-3" /></button>
          <button type="button" onClick={() => onMove(1)} disabled={index === count - 1} className="p-1 text-ink/45 hover:text-ink disabled:opacity-20"><ArrowDown className="w-3 h-3" /></button>
          <button type="button" onClick={onRemove} className="p-1 text-ink/45 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {option.values.map((v, i) => (
          <ValueChip key={i} value={v}
            onChange={nv => onChange({ ...option, values: option.values.map((x, j) => (j === i ? nv : x)) })}
            onRemove={() => onChange({ ...option, values: option.values.filter((_, j) => j !== i) })} />
        ))}
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addValues())}
          onBlur={addValues} placeholder={option.values.length ? 'Add more…' : 'Values, comma separated (S, M, L)'}
          className={`${smallInputCls} flex-1 min-w-[180px] !py-1 text-xs`} />
      </div>
    </div>
  );
}

export function OptionsEditor({ options = [], onChange }) {
  const update = (i, opt) => onChange(options.map((o, j) => (j === i ? opt : o)));
  const move = (i, dir) => {
    const next = [...options];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <datalist id="option-presets">{OPTION_PRESETS.map(p => <option key={p} value={p} />)}</datalist>
      {options.map((o, i) => (
        <OptionRow key={i} option={o} index={i} count={options.length}
          onChange={opt => update(i, opt)} onRemove={() => onChange(options.filter((_, j) => j !== i))} onMove={d => move(i, d)} />
      ))}
      {options.length < MAX_OPTIONS ? (
        <button type="button" onClick={() => onChange([...options, { name: OPTION_PRESETS.find(p => !options.some(o => o.name === p)) || '', values: [] }])} className={btnSecondary}>
          <Plus className="w-4 h-4" /> Add option ({options.length}/{MAX_OPTIONS})
        </button>
      ) : <p className="text-ink/45 text-xs">Maximum of {MAX_OPTIONS} options reached.</p>}
      {options.length > 0 && <p className="text-ink/45 text-xs">Shoppers must pick one value for each option. Use the +$ box for choices that cost more.</p>}
    </div>
  );
}

export function PersonalizationEditor({ fields = [], onChange }) {
  const update = (i, patch) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={i} className="p-3 rounded-xl border border-ink/10 grid grid-cols-12 gap-2 items-center">
          <input value={f.label} onChange={e => update(i, { label: e.target.value })} placeholder="Label, e.g. Name to engrave" className={`${smallInputCls} col-span-5`} />
          <input value={f.placeholder || ''} onChange={e => update(i, { placeholder: e.target.value })} placeholder="Hint (optional)" className={`${smallInputCls} col-span-3`} />
          <label className="col-span-2 flex items-center gap-1 text-ink/55 text-xs" title="Character limit">
            Max <input type="number" min="1" max="500" value={f.max_length} onChange={e => update(i, { max_length: parseInt(e.target.value) || 1 })} className={`${smallInputCls} w-14 !px-2`} />
          </label>
          <button type="button" onClick={() => onChange(fields.filter((_, j) => j !== i))} className="col-span-2 justify-self-end text-ink/45 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          <label className="col-span-5 flex items-center gap-2 text-ink/70 text-xs cursor-pointer">
            <input type="checkbox" checked={!!f.required} onChange={e => update(i, { required: e.target.checked })} className="accent-honey" /> Required
          </label>
          <label className="col-span-7 flex items-center gap-1 text-ink/55 text-xs">
            Extra charge +$ <input type="number" step="0.01" min="0" value={f.price_delta || ''} placeholder="0"
              onChange={e => update(i, { price_delta: e.target.value === '' ? 0 : parseFloat(e.target.value) })} className={`${smallInputCls} w-20 !px-2`} />
          </label>
        </div>
      ))}
      {fields.length < MAX_PERSONALIZATIONS ? (
        <button type="button" onClick={() => onChange([...fields, { label: '', max_length: 20, required: false, price_delta: 0, placeholder: '' }])} className={btnSecondary}>
          <Plus className="w-4 h-4" /> Add personalization field ({fields.length}/{MAX_PERSONALIZATIONS})
        </button>
      ) : <p className="text-ink/45 text-xs">Maximum of {MAX_PERSONALIZATIONS} personalization fields reached.</p>}
    </div>
  );
}
